import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { productData } from '../data/mockData';
import { log } from '../lib/logger';
import {
  clearActiveDraftOrderId,
  getActiveDraftOrderId,
  notifyActiveDraftChanged,
  setActiveDraftOrderId,
} from '../lib/activeDraftOrder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Extracted Place-Order flow for QuoteCalculator.
 *
 * Keeps the 70+ LOC orchestrator out of the 1000+ line component and lets
 * us test the submission logic in isolation. Also owns the post-guest-login
 * re-run effect (previously duplicated inline).
 *
 * @returns {{
 *   handlePlaceOrder: () => Promise<void>,
 *   pendingPlaceOrder: boolean,
 *   setPendingPlaceOrder: (v:boolean) => void,
 * }}
 */
export const useOrderSubmission = ({
  location,
  navigate,
  user,
  isAuthenticated,
  loading,
  isShippingAddressComplete,
  // Product selection
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  selectedShape,
  width,
  height,
  unit,
  quantity,
  shippingMethod,
  country,
  shippingAddress,
  subtotal,
  // Optional white-ink option for transparent / specialty products.
  // Only meaningful when isWhiteInkEligible(selectedFormat, selectedMaterial)
  // returned true at picker time. Stored on the order item so the
  // proof + print-file generators can apply the right rendering.
  whiteInkOption,
  // Page 2 artwork
  page2ArtworkFile,
  page2ArtworkId,
  page2CutlineShape,
  page2ArtworkInstructions,
  // Lookups / UI
  getAvailableMaterials,
  setGuestPromptOpen,
}) => {
  const [pendingPlaceOrder, setPendingPlaceOrder] = useState(false);
  // Guards against double-submit. `handlePlaceOrder` creates a fresh draft
  // + add-item + attach-artwork sequence; if the user double-clicks while
  // that's in flight (especially on slow connections), we'd otherwise spawn
  // N duplicate orders and race on staging-attach (which 404s after the
  // first adoption) — losing the artwork on the order the user lands on.
  const [submitting, setSubmitting] = useState(false);

  /**
   * Persist the Page 2 staged artwork into the order.
   *
   * Fast path (page2ArtworkId present — staging-upload succeeded):
   *   POST /api/files/artwork/staging/{id}/attach — zero re-upload bandwidth.
   *
   * Fallback (staging upload failed but the File is still in memory):
   *   POST /api/upload-artwork — re-upload from scratch. Same as the
   *   legacy single-item flow.
   */
  const persistPage2Artwork = useCallback(
    async (orderId, newItemId) => {
      if (!page2ArtworkFile) return;

      try {
        if (page2ArtworkId) {
          // Fast path — already uploaded during the drop; just attach.
          const form = new FormData();
          form.append('order_id', orderId);
          if (newItemId) form.append('item_id', newItemId);
          if (page2CutlineShape) form.append('cutline_shape', page2CutlineShape);
          await axios.post(
            `${API_URL}/api/files/artwork/staging/${page2ArtworkId}/attach`,
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
        } else {
          // Fallback — staging upload failed; re-upload during place-order.
          const form = new FormData();
          form.append('order_id', orderId);
          form.append('files', page2ArtworkFile);
          if (page2CutlineShape) form.append('cutline_shape', page2CutlineShape);
          if (newItemId) form.append('item_id', newItemId);
          await axios.post(
            `${API_URL}/api/files/upload-artwork`,
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
        }

        // Persist free-form artwork notes on the draft order itself so
        // we don't have to bridge user-authored text through sessionStorage
        // (which is XSS-visible + sticks around on shared devices).
        const notes = (page2ArtworkInstructions || '').trim();
        if (notes) {
          try {
            await axios.post(
              `${API_URL}/api/enhanced-orders/${orderId}/artwork-instructions-draft`,
              { artwork_instructions: notes },
            );
          } catch (noteErr) {
            // Non-fatal — ReviewOrderPage still has a notes field the
            // customer can fill in before Submit.
            log.warn('Could not save artwork instructions draft:', noteErr);
          }
        }
      } catch (err) {
        // Non-fatal — customer can still upload on the dedicated page.
        log.warn('Page 2 artwork persist failed, falling back to /upload-artwork:', err);
      }
    },
    [page2ArtworkFile, page2ArtworkId, page2CutlineShape, page2ArtworkInstructions],
  );

  const handlePlaceOrder = useCallback(async () => {
    // Wait for auth to finish loading
    if (loading) return;

    // Unauthenticated visitors get a guest-checkout prompt instead of an
    // alert-and-redirect. They can place the order after dropping just an
    // email; account claiming happens later on the confirmation page.
    if (!isAuthenticated || !user) {
      setGuestPromptOpen(true);
      return;
    }

    // Validate shipping address is complete on Step 6
    if (!isShippingAddressComplete()) {
      toast.warning('Please complete your shipping address', {
        description: 'Contact name, address, city, state/province, and postal code are required before placing your order.',
      });
      return;
    }

    // Friendly nudge: artwork is optional at place-order (the customer
    // can upload it later via /upload-artwork or admin proof-revision),
    // but reminding them now means fewer "where did my proof go?" emails
    // 24 hours later. Toast only — does NOT block submission.
    if (!page2ArtworkFile && !page2ArtworkId) {
      toast.info('Heads up — no artwork uploaded yet', {
        description: 'You can still place the order; we\'ll prompt you to upload artwork on the next page.',
        duration: 4000,
      });
    }

    // Double-submit guard — a rapid second click while the first request
    // chain is still in flight would otherwise spawn a duplicate draft
    // order (we've seen 7 drafts in 5ms in the wild).
    if (submitting) return;
    setSubmitting(true);
    try {
      const navStateOrderId = location.state?.orderId;
      const navAddToExisting = location.state?.addToExisting;
      // sessionStorage acts as the resilient fallback: if the customer
      // refreshed the page or navigated via top-nav, location.state is
      // gone but the persistent draft id keeps the cart intact.
      const sessionOrderId = getActiveDraftOrderId();
      const existingOrderId = navStateOrderId || sessionOrderId;
      // We treat any *known* existing order as "add to existing" — the
      // backend's create_or_reuse_draft validates ownership + 404s on
      // mismatch, so this is safe even if the session value is stale
      // (we just fall through to creating a fresh draft on the retry).
      const addToExisting = Boolean(navAddToExisting || (sessionOrderId && !navStateOrderId));

      const category = productData.categories.find((c) => c.id === selectedProduct);
      const subcategory = category?.subcategories.find((s) => s.id === selectedFormat);
      const material = getAvailableMaterials().find((m) => m.id === selectedMaterial);

      // Page-Maker eligibility: multi-design auto-tiled onto a letter sheet.
      const isPageMaker = !!(subcategory?.pageMaker || material?.pageMaker);

      const itemPayload = {
        product_type: `${category?.name} - ${subcategory?.name}` || 'Unknown',
        format: selectedFormat || '',
        material: material?.name || selectedMaterial,
        size: `${width}x${height} ${unit}`,
        width: parseFloat(width),
        height: parseFloat(height),
        unit,
        quantity,
        price: subtotal,
        shape: selectedShape,
        shipping_method: shippingMethod,
        country,
        page_maker: isPageMaker,
        // Persist the white-ink toggle so admin / proof / print-file
        // generators downstream can branch. Only set when the picker
        // was actually displayed for this product (otherwise omit so
        // the field doesn't pollute non-eligible items).
        ...(whiteInkOption ? { white_ink_option: whiteInkOption } : {}),
      };

      // Single compound call — creates the draft (or reuses the existing
      // one), pushes the item, and atomically adopts the staging artwork.
      // Replaces the 3-POST chain that used to live here.
      let placeResp;
      try {
        placeResp = await axios.post(
          `${API_URL}/api/enhanced-orders/place`,
          {
            item: itemPayload,
            existing_order_id: addToExisting && existingOrderId ? existingOrderId : null,
            staging_artwork_id: page2ArtworkId || null,
            cutline_shape: page2CutlineShape || null,
          },
        );
      } catch (placeErr) {
        // Stale session draft id (e.g. order was already submitted in
        // another tab) → 404. Clear it and retry as a fresh order so the
        // customer doesn't get stuck with no path forward.
        if (
          placeErr.response?.status === 404 &&
          addToExisting &&
          existingOrderId
        ) {
          log.warn('Stale draft order id; clearing session and retrying as a fresh draft.');
          clearActiveDraftOrderId();
          placeResp = await axios.post(
            `${API_URL}/api/enhanced-orders/place`,
            {
              item: itemPayload,
              existing_order_id: null,
              staging_artwork_id: page2ArtworkId || null,
              cutline_shape: page2CutlineShape || null,
            },
          );
        } else {
          throw placeErr;
        }
      }
      const { order_id: orderId, order_number: orderNumber, item_id: newItemId, artwork_attached } = placeResp.data;

      // Persist the active draft id so future "Add new item" clicks
      // append to this same draft even if location.state is dropped
      // (hard refresh, top-nav, etc.). Cleared on submit / logout.
      setActiveDraftOrderId(orderId);
      // Even when orderId didn't change (appending another item to the
      // same draft), tell the cart badge to refresh its item count.
      notifyActiveDraftChanged();

      // Fire-and-forget the post-/place enrichment work so navigation to
      // the Review page happens IMMEDIATELY. Each of these tasks would
      // previously block navigation behind sequential `await`s, adding
      // 200-2000ms of perceived latency on slow networks (sometimes
      // tripping the customer's "looks frozen, let me refresh" reflex
      // and spawning duplicate orders). The Review page can hydrate from
      // the freshly-saved server state on its initial GET — none of
      // these tasks gate first paint.

      // (1) Artwork fallback re-upload — only fires on the rare path
      // where staging-upload failed at drop-time. The customer can
      // still upload artwork later from the Review page if this
      // background task fails, so we don't block on it.
      if (page2ArtworkFile && !artwork_attached) {
        persistPage2Artwork(orderId, newItemId).catch((err) => {
          log.warn('Background artwork persist failed (non-fatal):', err);
        });
      } else if (page2ArtworkInstructions && (page2ArtworkFile || page2ArtworkId)) {
        // (2) Artwork notes save — fast (<100ms) but still no reason to
        // block navigation. Server-truth: ReviewOrderPage will see the
        // saved value on next GET / refresh.
        const notes = (page2ArtworkInstructions || '').trim();
        if (notes) {
          axios
            .post(
              `${API_URL}/api/enhanced-orders/${orderId}/artwork-instructions-draft`,
              { artwork_instructions: notes },
            )
            .catch((noteErr) => {
              log.warn('Background artwork-instructions save failed (non-fatal):', noteErr);
            });
        }
      }

      // Claim any Live Mockup previews the customer generated during
      // this quote session — associates `cache_key`s with the order so
      // the clean (watermark-free) PNG can be downloaded from PO History.
      //
      // Fire-and-forget: this is non-critical and adds 200-800ms of
      // perceived latency if awaited before navigation. The customer
      // shouldn't have to wait on a preview cache-key adoption to reach
      // their Review page. Errors are still logged for observability.
      try {
        const K = 'eg3_pending_preview_keys';
        const keys = JSON.parse(window.sessionStorage.getItem(K) || '[]');
        if (Array.isArray(keys) && keys.length) {
          // Intentionally NOT awaited — see comment above.
          axios
            .post(`${API_URL}/api/product-preview/claim`, {
              order_id: orderId,
              cache_keys: keys,
            })
            .catch((claimErr) => {
              log.warn('Could not claim preview cache keys (non-fatal):', claimErr);
            });
        }
        window.sessionStorage.removeItem(K);
      } catch (claimErr) {
        log.warn('Could not claim preview cache keys (non-fatal):', claimErr);
      }

      // Always land on the Review Order page after Place Order. Artwork
      // uploaded on Page 2 is already attached via the /place compound
      // endpoint. If no artwork was provided, the Review Order page shows
      // a prominent "Upload artwork" CTA next to the proof preview so
      // customers are nudged once (not forced through a separate step).
      // Eliminates the "I already uploaded, why am I being asked again?"
      // surprise when Page 2 staging worked correctly.
      const navState = {
        orderId,
        orderNumber,
        itemId: newItemId,
        shippingAddress,
        country,
      };
      if (addToExisting && existingOrderId) navState.addToExisting = true;
      // Use the path-param URL so a customer-initiated refresh (or a
      // bookmarked link from email) lands back on the same order
      // instead of bouncing to /quote.
      navigate(`/review-order/${orderId}`, { state: navState });
      // Intentionally leave `submitting` = true on the success path.
      // navigate() schedules an unmount of QuoteCalculator; if we flip
      // the flag in `finally` the PlaceOrderLoadingOverlay would briefly
      // disappear before the unmount lands, producing a 1-frame flash
      // back to the wizard. Letting it unmount with the page is cleaner.
      return;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        log.error('Order creation error:', error);
      }
      // Reset the submitting flag on error so the overlay clears and the
      // customer can retry. (Success path returns early above.)
      setSubmitting(false);
      if (error.response?.status === 401) {
        toast.error('Your session has expired', {
          description: 'Redirecting to login…',
        });
        navigate('/login');
        return;
      }
      toast.error('Failed to create order', {
        description: error.response?.data?.detail || error.message || 'Please try again.',
      });
    }
  }, [
    loading,
    isAuthenticated,
    user,
    isShippingAddressComplete,
    location.state,
    navigate,
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    selectedShape,
    width,
    height,
    unit,
    quantity,
    subtotal,
    shippingMethod,
    country,
    shippingAddress,
    page2ArtworkFile,
    page2ArtworkId,
    page2CutlineShape,
    page2ArtworkInstructions,
    whiteInkOption,
    getAvailableMaterials,
    persistPage2Artwork,
    setGuestPromptOpen,
    submitting,
  ]);

  // After GuestCheckoutPrompt successfully mints a guest user, AuthContext
  // asynchronously commits it. Re-run handlePlaceOrder once the new user
  // arrives so the caller doesn't have to click Place Order twice.
  useEffect(() => {
    if (!pendingPlaceOrder) return;
    if (loading || !isAuthenticated || !user) return;
    setPendingPlaceOrder(false);
    Promise.resolve().then(() => handlePlaceOrder());
    // handlePlaceOrder intentionally excluded — it's recreated every render
    // and would trigger an infinite loop. We only want to fire on the
    // loading/auth transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPlaceOrder, loading, isAuthenticated, user]);

  return { handlePlaceOrder, pendingPlaceOrder, setPendingPlaceOrder, submitting };
};

export default useOrderSubmission;
